import React, { Component } from 'react'
import { Form, HelpBlock } from 'react-bootstrap'
import '../App.css'
import { embedComponent } from '../embed'
import Field from '../components/Field'
import CurrencyField from '../components/CurrencyField'
import formatCurrency from '../utils/formatters/formatCurrency'
import numberWithLabelChoices from '../utils/forms/numberWithLabelChoices'
import requiredPaymentToGoal from '../utils/calculations/requiredPaymentToGoal'
import CalculatorResults from '../components/CalculatorResults/index'
import CalculatorResult from '../components/CalculatorResult/index'
import Calculator from '../components/Calculator/index'
import PercentageField from '../components/PercentageField/index'
import CalculatorButtons from '../components/CalculatorButtons/index'
import { minMaxValidator } from '../utils/validators/index'
import formatPercentage from '../utils/formatters/formatPercentage'
import Annotations from '../components/Annotations/index'
import { DEFAULT_MAX_DOLLAR_VALUE } from '../utils/validators'

const popoverMessages = {
  monthlyTakeHomePay: {
    title: 'Monthly take home pay',
    body: [
      'This is the amount you take home each month after taxes and other deductions such as health insurance and 401k.',
      'Typically, it is about 75% of your total gross monthly earnings.',
    ],
  },
  monthsOfSavings: {
    title: 'Months of savings',
    body: [
      'This is the number of months of savings you want to have put away in case of emergency.',
      'Most experts recommend six months of savings. <a href="/#/articles/64" target="_blank">Click here to learn more about emergency savings</a>.',
    ],
  },
  savingsGoal: {
    title: 'Savings goal',
    body: [
      'This is the total amount you want to have in an emergency savings account.',
      'The calculator assumes that fixed expenses constitute 50% of take home pay and multiplies this by the months selected.',
    ],
  },
  currentSavings: {
    title: 'Current savings',
    body: [
      'This is the amount you have now in your emergency fund. It does not include other savings like 401k or stocks.',
    ],
  },
  timeToGoal: {
    title: 'Time to goal',
    body: [
      'This is the time you are giving yourself to reach your savings goal.',
      'The shorter you make it, the higher the monthly contributions required will be.',
    ],
  },
  interestOnSavings: {
    title: 'Interest on savings',
    body: [
      'This is the interest rate you get on your savings.',
    ],
  },
  monthlySavingsAmount: {
    title: 'You need to save this amount/month',
    body: [
      'This is the amount you need to set aside each month for the number of months you indicated.',
    ],
  },
}

// Create select input options for months dropdowns.
const monthsOfSavingsValues = [3, 6, 12, 18, 24, 36]
const timeToGoalValues = monthsOfSavingsValues.concat([48, 60])
const monthsOfSavingsOptions = numberWithLabelChoices(monthsOfSavingsValues, ' months')
const timeToGoalOptions = numberWithLabelChoices(timeToGoalValues, ' months')

class EmergencySavingsCalculator extends Component {
  constructor (props) {
    super(props)

    this.state = EmergencySavingsCalculator.getInitialState()

    this.clearState = this.clearState.bind(this)
    this.onValidation = this.onValidation.bind(this)
  }

  static getInitialState() {
    return {
      monthlyTakeHomePay: null,
      monthsOfSavings: 6,
      savingsGoal: 0,
      currentSavings: null,
      timeToGoal: 24,
      interestOnSavings: null,
      fieldErrors: {},
    }
  }

  handleChange (value, fieldName) {
    this.setState({ [fieldName]: value })
  }

  clearState () {
    this.setState(EmergencySavingsCalculator.getInitialState())
  }

  onValidation(name, error) {
    if (typeof error !== 'undefined') {
      // Has errors
      if (!this.state.fieldErrors[name]) {
        this.setState({ fieldErrors: Object.assign({}, this.state.fieldErrors, { [name]: error }) })
      }
    } else {
      // No errors
      if (this.state.fieldErrors[name]) {
        const newErrors = Object.assign({}, this.state.fieldErrors)
        delete newErrors[name]
        this.setState({ fieldErrors: newErrors })
      }
    }
  }

  calculateMonthlySavingsAmount () {
    const { currentSavings, interestOnSavings, savingsGoal, timeToGoal } = this.state

    const rawSavingsGoal = savingsGoal || this.calculateSavingsGoal()

    if (!rawSavingsGoal || !interestOnSavings) {
      return undefined
    }

    return requiredPaymentToGoal(rawSavingsGoal, interestOnSavings / 100, timeToGoal / 12, currentSavings || 0)
  }

  /**
   * Monthly take home pay * 50% * Months of savings
   */
  calculateSavingsGoal () {
    const { monthlyTakeHomePay, monthsOfSavings } = this.state

    if (!monthlyTakeHomePay || !monthsOfSavings) {
      return undefined
    }

    return monthlyTakeHomePay * .5 * monthsOfSavings;
  }

  render () {
    const {
      monthlyTakeHomePay,
      monthsOfSavings,
      savingsGoal,
      currentSavings,
      timeToGoal,
      interestOnSavings,
    } = this.state

    const calculatedMonthlySavingsAmount = this.calculateMonthlySavingsAmount();
    const calculatedSavingsGoal = this.calculateSavingsGoal();
    const hasErrors = Object.keys(this.state.fieldErrors).length > 0

    return (
      <Calculator title="Emergency Savings Calculator">
        <div className="calculator-body">
          <Form>
            <CurrencyField
              name="monthlyTakeHomePay"
              label="Monthly Take Home Pay"
              value={monthlyTakeHomePay}
              onChange={(e, value) => this.handleChange(value, 'monthlyTakeHomePay') }
              popover={popoverMessages.monthlyTakeHomePay}
              validator={minMaxValidator(1, DEFAULT_MAX_DOLLAR_VALUE, formatCurrency)}
              onValidation={this.onValidation}
            />

            <Field
              type="select"
              name="monthsOfSavings"
              label="Months of Savings"
              value={monthsOfSavings}
              choices={monthsOfSavingsOptions}
              onChange={(e, value) => this.handleChange(value, 'monthsOfSavings') }
              popover={popoverMessages.monthsOfSavings}
            />

            <CurrencyField
              name="savingsGoal"
              label="Savings Goal"
              annotationLabel="*"
              value={savingsGoal}
              calculatedValue={calculatedSavingsGoal}
              onChange={(e, value) => this.handleChange(value, 'savingsGoal') }
              popover={popoverMessages.savingsGoal}
            />

            <CurrencyField
              name="currentSavings"
              label="Current Savings"
              value={currentSavings}
              onChange={(e, value) => this.handleChange(value, 'currentSavings') }
              popover={popoverMessages.currentSavings}
              validator={minMaxValidator(1, DEFAULT_MAX_DOLLAR_VALUE, formatCurrency)}
              onValidation={this.onValidation}
            />

            <Field
              type="select"
              name="timeToGoal"
              label="Time to Goal"
              value={timeToGoal}
              onChange={(e, value) => this.handleChange(value, 'timeToGoal')}
              popover={popoverMessages.timeToGoal}
              choices={timeToGoalOptions}
            />

            <PercentageField
              name="interestOnSavings"
              label="Interest on Savings (%)"
              value={interestOnSavings}
              onChange={(e, value) => this.handleChange(value, 'interestOnSavings') }
              popover={popoverMessages.interestOnSavings}
              validator={minMaxValidator(0.001, 30, formatPercentage)}
              onValidation={this.onValidation}
            />
          </Form>
          <CalculatorResults>
            <CalculatorResult
              label={'You need to save this amount/month'}
              value={formatCurrency(Math.ceil((!hasErrors && calculatedMonthlySavingsAmount) || 0))}
              popover={popoverMessages.monthlySavingsAmount}
            />
          </CalculatorResults>
          <CalculatorButtons onClear={this.clearState} />
          <Annotations>
            <HelpBlock><sup>*</sup> Assumes one month of fixed expenses equals 50% of take home pay.</HelpBlock>
          </Annotations>
        </div>
      </Calculator>
    )
  }
}

embedComponent(EmergencySavingsCalculator, 'tgm-emergency-savings-calc', 'tgm-emergency-savings-calc-embed')

export default EmergencySavingsCalculator



// WEBPACK FOOTER //
// ./src/calculators/EmergencySavingsCalculator.js